import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Share2, Upload, Search, Trash2 } from "lucide-react";
import { fetchLegacyProposals as fetchProposalsApi, updateLegacyProposalOwner as updateProposalOwner, deleteLegacyProposal as deleteProposal, deleteAllLegacyProposals as deleteAllProposals } from "@/lib/legacyProposalsApi";
import { listUsers } from "@/lib/usersApi";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useToast } from "@/hooks/use-toast";
import {
  PROPOSAL_STATUS_FLOW,
  PROPOSAL_STATUS_LABELS,
  canShowShareButton,
} from "@/lib/proposalStatus";
import { ProposalStatusBadge } from "@/components/proposals/ProposalStatusBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LegacyProposals = () => {
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [employees, setEmployees] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isCeo = userRole === "ceo";
  const isAdmin = userRole === "ceo" || userRole === "general_manager";
  const isSales = userRole === "sales_staff";

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value, 10));
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, ownerFilter, priorityFilter]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (isSales && user?.id) {
      setOwnerFilter(user.id);
    }
  }, [isSales, user?.id]);

  useEffect(() => {
    fetchProposals();
  }, [currentPage, pageSize, searchQuery, statusFilter, ownerFilter, priorityFilter]);

  const fetchEmployees = async () => {
    try {
      const users = await listUsers();
      setEmployees(users || []);
    } catch {
      setEmployees([]);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await fetchProposalsApi({
        page: currentPage,
        size: pageSize,
        search: searchQuery.trim() || undefined,
        sortField: "created_at",
        sortOrder: "desc",
        status: statusFilter !== "all" ? statusFilter : undefined,
        priority: priorityFilter !== "all" ? priorityFilter : undefined,
        ownerId: isSales && user?.id ? user.id : ownerFilter !== "all" ? ownerFilter : undefined,
      });
      setProposals(response.items || []);
      setTotalCount(response.total || 0);
    } catch (error: any) {
      toast({
        title: "Error loading proposals",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, any> = {
      high: "destructive",
      medium: "secondary",
      low: "outline",
    };
    return <Badge variant={variants[priority] || "secondary"}>{priority?.toUpperCase() || "MEDIUM"}</Badge>;
  };

  const getClientTypeBadge = (type?: string) => {
    const key = (type || "final_client").toLowerCase();
    const variants: Record<string, any> = {
      agency: "default",
      final_client: "secondary",
      operator: "destructive",
      partner: "outline",
    };
    const labels: Record<string, string> = {
      agency: "Agency",
      final_client: "Final Client",
      operator: "Operator",
      partner: "Partner",
    };
    return <Badge variant={variants[key] || "secondary"}>{labels[key] || type || "Final Client"}</Badge>;
  };

  const getProposalClientProfile = (proposal: any): "agency" | "operator" | "final_client" | "partner" => {
    const partnerType = (proposal?.clients?.partner_business_type ?? proposal?.clients?.partnerBusinessType ?? "")
      .toString()
      .toLowerCase();
    const partnerId = proposal?.clients?.partner_id ?? proposal?.clients?.partnerId;
    const hasPartner = partnerId !== null && partnerId !== undefined;

    if (partnerType === "operator") return "operator";
    if (partnerType === "agency" || partnerType === "agent") return "agency";
    if (!hasPartner) return "final_client";
    return "partner";
  };

  const renderClientTypeCell = (proposal: any) => {
    const type = getProposalClientProfile(proposal);
    const operatorName =
      proposal?.clients?.operator_agency_name ??
      proposal?.clients?.operatorAgencyName ??
      proposal?.clients?.operator_agent_name;
    const showName = (type === "agency" || type === "operator") && operatorName;
    return (
      <div className="flex flex-col items-start gap-1">
        {getClientTypeBadge(type)}
        {showName && (
          <div className="text-xs text-muted-foreground leading-tight">
            {operatorName}
          </div>
        )}
      </div>
    );
  };

  const handleOwnerChange = async (proposalId: string, newOwnerId: string) => {
    try {
      await updateProposalOwner(proposalId, newOwnerId);
    } catch (error: any) {
      toast({
        title: "Error updating owner",
        description: error?.message || "Failed to update owner.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Owner updated",
      description: "The proposal owner has been changed successfully.",
    });

    fetchProposals();
  };

  const handleShare = (proposalId: string) => {
    const link = `${window.location.origin}/proposal/${proposalId}`;
    setShareableLink(link);
    setShowShareDialog(true);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareableLink);
    toast({
      title: "Link copied!",
      description: "The proposal link has been copied to clipboard.",
    });
  };

  const openProposal = () => {
    window.open(shareableLink, "_blank");
  };

  const handleDeleteClick = (proposal: any) => {
    setProposalToDelete(proposal);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!proposalToDelete) return;

    try {
      setDeleting(true);
      await deleteProposal(proposalToDelete.id);

      toast({
        title: "Proposal deleted",
        description: `Proposal "${proposalToDelete.title}" has been deleted successfully.`,
      });

      setDeleteDialogOpen(false);
      setProposalToDelete(null);
      fetchProposals();
    } catch (error: any) {
      toast({
        title: "Error deleting proposal",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllConfirm = async () => {
    try {
      setDeletingAll(true);
      if (totalCount === 0) {
        toast({ title: "No proposals to delete", description: "The list is already empty." });
        setDeleteAllDialogOpen(false);
        return;
      }
      await deleteAllProposals();
      toast({
        title: "All proposals deleted",
        description: "All proposal data has been removed from this screen.",
      });
      setDeleteAllDialogOpen(false);
      fetchProposals();
    } catch (error: any) {
      toast({
        title: "Error deleting proposals",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Proposals (Legacy)</h1>
            <p className="text-muted-foreground">
              Imported proposals from file (legacy base)
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={() => navigate("/proposals-legacy/import")}>
                <Upload className="mr-2 h-4 w-4" />
                Import Proposals
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search proposals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {PROPOSAL_STATUS_FLOW.map((status) => (
                <SelectItem key={status} value={status}>
                  {PROPOSAL_STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isSales && (
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <Table className="min-w-[1200px]">
            <TableHeader>
              <TableRow>
                <TableHead>Internal Reference</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                {isAdmin && <TableHead>Owner</TableHead>}
                <TableHead>Priority</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Total Value</TableHead>
                <TableHead>Created</TableHead>
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 11 : 9} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : totalCount === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 11 : 9} className="text-center">
                    {searchQuery ? "No legacy proposals match your search" : "No legacy proposals found"}
                  </TableCell>
                </TableRow>
              ) : (
                proposals.map((proposal) => (
                  <TableRow 
                    key={proposal.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/proposal/${proposal.id}`)}
                  >
                    <TableCell className="font-medium">
                      {proposal.internal_reference || "-"}
                    </TableCell>
                    <TableCell>{proposal.title}</TableCell>
                    <TableCell>
                      {proposal.clients?.company ||
                        `${proposal.clients?.first_name} ${proposal.clients?.last_name}`}
                    </TableCell>
                    <TableCell>{renderClientTypeCell(proposal)}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        {employees.find(e => e.id === proposal.owner_id)?.full_name || "-"}
                      </TableCell>
                    )}
                    <TableCell>{getPriorityBadge(proposal.priority)}</TableCell>
                    <TableCell>{proposal.main_destination}</TableCell>
                    <TableCell><ProposalStatusBadge status={proposal.status} size="sm" /></TableCell>
                    <TableCell className="font-medium">
                      ${proposal.total_value?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell>
                      {new Date(proposal.created_at).toLocaleDateString()}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2 items-center">
                          {canShowShareButton(proposal.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(proposal.id);
                              }}
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                          )}
                          {isCeo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(proposal);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Select
                            value={proposal.owner_id || ""}
                            onValueChange={(value) => handleOwnerChange(proposal.id, value)}
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Change owner" />
                            </SelectTrigger>
                            <SelectContent>
                              {employees.map((employee) => (
                                <SelectItem key={employee.id} value={employee.id}>
                                  {employee.full_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalCount > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-sm text-muted-foreground">
                Showing {totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm">
                Page {currentPage} of {totalPages || 1}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(currentPage + 1, Math.max(totalPages, 1)))}
                disabled={currentPage >= totalPages || loading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Shareable Proposal</DialogTitle>
              <DialogDescription>
                The proposal has been successfully finalized! Use the link below to share with the client.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-md break-all text-sm">
                {shareableLink}
              </div>
              <div className="flex gap-2">
                <Button onClick={copyLink} className="flex-1">
                  Copy Link
                </Button>
                <Button onClick={openProposal} variant="outline" className="flex-1">
                  Open Proposal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Proposal</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the proposal "{proposalToDelete?.title}"? 
                This action cannot be undone and will permanently remove all associated data 
                (accommodations, tours, transfers, flights, etc.).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? "Deleting..." : "Delete Proposal"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all proposals</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all proposals and their related data from this screen. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllConfirm}
                disabled={deletingAll}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deletingAll ? "Deleting..." : "Delete all"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default LegacyProposals;
